<div class="container mx-auto px-4 py-8 max-w-4xl">
  <!-- Header -->
  <div class="mb-8">
    <h1 class="text-3xl font-bold text-gray-800 dark:text-white mb-2">
      Delete Your Account
    </h1>
    <p class="text-gray-600 dark:text-gray-400">
      Permanently delete your Wall St Yardie account and all associated data.
    </p>
  </div>

  <!-- Messages -->
  @if (errorMessage()) {
    <p-message severity="error" [text]="errorMessage()!" styleClass="mb-4 w-full"></p-message>
  }
  
  @if (successMessage()) {
    <p-message severity="success" [text]="successMessage()!" styleClass="mb-4 w-full"></p-message>
  }

  <!-- Pending Deletion Warning -->
  @if (deletionRequest()) {
    <div class="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg p-6 mb-8">
      <div class="flex items-start">
        <i class="pi pi-exclamation-triangle text-3xl text-red-600 dark:text-red-400 mr-4"></i>
        <div class="flex-1">
          <h2 class="text-xl font-bold text-red-900 dark:text-red-100 mb-2">
            Account Deletion Pending
          </h2>
          <p class="text-red-800 dark:text-red-200 mb-4">
            Your account is scheduled for permanent deletion in <strong>{{ getDaysRemaining() }} days</strong>.
          </p>
          <p class="text-red-800 dark:text-red-200 mb-4">
            Deletion date: <strong>{{ deletionRequest()!.scheduledDeletionDate | date:'fullDate' }}</strong>
          </p>
          <p class="text-red-700 dark:text-red-300 text-sm mb-4">
            After this date, all your data will be permanently deleted and cannot be recovered.
          </p>
          <p-button
            label="Cancel Deletion"
            icon="pi pi-times"
            severity="danger"
            [loading]="isCanceling()"
            [disabled]="isCanceling()"
            (onClick)="cancelDeletion()"
          ></p-button>
        </div>
      </div>
    </div>
  }

  <!-- No Pending Deletion -->
  @if (!deletionRequest()) {
    <!-- Warning Box -->
    <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-6 mb-8">
      <h2 class="text-xl font-semibold text-yellow-900 dark:text-yellow-100 mb-3">
        <i class="pi pi-exclamation-triangle mr-2"></i>Warning: This Action Cannot Be Undone
      </h2>
      <p class="text-yellow-800 dark:text-yellow-200 mb-2">
        Deleting your account will permanently remove:
      </p>
      <ul class="text-yellow-800 dark:text-yellow-200 space-y-1 ml-6 list-disc">
        <li>Your profile and account information</li>
        <li>All watchlists and saved stocks</li>
        <li>Your preferences and settings</li>
        <li>Activity history and analytics</li>
        <li>Any active subscriptions</li>
      </ul>
    </div>

    <!-- Grace Period Info -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        30-Day Grace Period
      </h2>
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        When you request account deletion, we'll schedule your account for permanent deletion 30 days from now. During this grace period:
      </p>
      <ul class="text-gray-600 dark:text-gray-400 space-y-2 ml-6 list-disc mb-4">
        <li>Your account will be immediately deactivated</li>
        <li>You won't be able to log in or access your data</li>
        <li>You can cancel the deletion request at any time</li>
        <li>After 30 days, all data will be permanently deleted</li>
      </ul>
      <p class="text-gray-600 dark:text-gray-400">
        This grace period gives you time to change your mind and recover your account if needed.
      </p>
    </div>

    <!-- What Happens Section -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        What Happens When You Delete Your Account
      </h2>
      <div class="space-y-4">
        <div class="flex items-start">
          <div class="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span class="text-red-600 dark:text-red-400 font-bold">1</span>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">Immediate Deactivation</h3>
            <p class="text-gray-600 dark:text-gray-400 text-sm">
              Your account is immediately deactivated and you'll be logged out.
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <div class="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span class="text-red-600 dark:text-red-400 font-bold">2</span>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">Email Confirmation</h3>
            <p class="text-gray-600 dark:text-gray-400 text-sm">
              You'll receive an email confirming your deletion request with instructions to cancel if needed.
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <div class="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span class="text-red-600 dark:text-red-400 font-bold">3</span>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">30-Day Waiting Period</h3>
            <p class="text-gray-600 dark:text-gray-400 text-sm">
              Your data is retained for 30 days in case you change your mind.
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <div class="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span class="text-red-600 dark:text-red-400 font-bold">4</span>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">Permanent Deletion</h3>
            <p class="text-gray-600 dark:text-gray-400 text-sm">
              After 30 days, all your data is permanently deleted and cannot be recovered.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Alternatives Section -->
    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
      <h2 class="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-3">
        <i class="pi pi-info-circle mr-2"></i>Consider These Alternatives
      </h2>
      <p class="text-blue-800 dark:text-blue-200 mb-3">
        Before deleting your account, you might want to consider:
      </p>
      <ul class="text-blue-800 dark:text-blue-200 space-y-2 ml-6 list-disc">
        <li><strong>Downgrade to Free:</strong> Keep your account but cancel your subscription</li>
        <li><strong>Export Your Data:</strong> Download a copy of your data before deleting</li>
        <li><strong>Take a Break:</strong> Simply log out and come back when you're ready</li>
      </ul>
    </div>

    <!-- Delete Button -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
      <p class="text-gray-600 dark:text-gray-400 mb-6">
        If you're sure you want to delete your account, click the button below.
      </p>
      <p-button
        label="Delete My Account"
        icon="pi pi-trash"
        severity="danger"
        (onClick)="openConfirmDialog()"
      ></p-button>
    </div>
  }

  <!-- Confirmation Dialog -->
  <p-dialog
    header="Confirm Account Deletion"
    [(visible)]="showConfirmDialog"
    [modal]="true"
    [style]="{ width: '500px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="py-4">
      <p class="text-gray-700 dark:text-gray-300 mb-4">
        Are you absolutely sure you want to delete your account? This action will:
      </p>
      <ul class="text-gray-700 dark:text-gray-300 space-y-2 ml-6 list-disc mb-6">
        <li>Immediately deactivate your account</li>
        <li>Schedule permanent deletion in 30 days</li>
        <li>Remove all your data after the grace period</li>
      </ul>
      
      <div class="flex items-start mb-4">
        <p-checkbox
          [(ngModel)]="confirmationChecked"
          [binary]="true"
          inputId="confirm-deletion"
          class="mr-2"
        ></p-checkbox>
        <label for="confirm-deletion" class="text-gray-700 dark:text-gray-300 cursor-pointer">
          I understand that this action cannot be undone and my data will be permanently deleted after 30 days.
        </label>
      </div>
    </div>
    
    <ng-template pTemplate="footer">
      <p-button
        label="Cancel"
        icon="pi pi-times"
        severity="secondary"
        [outlined]="true"
        (onClick)="closeConfirmDialog()"
      ></p-button>
      <p-button
        label="Yes, Delete My Account"
        icon="pi pi-trash"
        severity="danger"
        [loading]="isDeleting()"
        [disabled]="!confirmationChecked() || isDeleting()"
        (onClick)="requestDeletion()"
      ></p-button>
    </ng-template>
  </p-dialog>
</div>
